import React, { useState } from 'react'
import { Meteor } from 'meteor/meteor'
import { FormattedMessage } from 'react-intl'
import useReactRouter from 'use-react-router'

import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ExpandMore from '@material-ui/icons/ExpandMore'
import makeStyles from '@material-ui/styles/makeStyles'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import PermIdentityOutlinedIcon from '@material-ui/icons/PermIdentityOutlined'
import useIsMobile from '../hooks/useIsMobile'
import useTracker from '../lib/useTracker'

import { useGlobalState } from '../store'
import { formattedMessage } from '../CurrentLocale'
import type { GlobalState } from '../types/globalState'
import { useGetSubRoutes } from '../lib/routes'
import Button from './Button'
import SearchNewContainer from './fragments/SearchNewContainer'
import Logo from './Logo'

const WIDTH_MENU = 210

type PropsSubMenu = {
  title: any,
  routes: Array<{
    title: string,
    link: string,
    typeRoute: string,
    path: string,
    function: Function,
  }>,
  testId?: string,
  theme: Object,
  classesSubMenu: Object,
}

const useStyles = makeStyles((themeMui) => ({
  root: ({ theme, showSearchBar }) => ({
    color: theme.colors.primaryText,
    overflow: 'hidden',
    width: '100%',
    zIndex: 2,
    position: 'relative',
    fontWeight: 'bold',
    borderBottom: `1px solid #d5dee6`,
    paddingBottom: showSearchBar ? 18 : 0,
    background:
      theme.colors.backgroundHeader ||
      `transparent linear-gradient(180deg, #FFFFFF 0%, #F5F6FA 100%) 0% 0% no-repeat padding-box`,
  }),
  firstLine: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    fontSize: 16,
  },
  titleUser: {
    display: 'flex',
    justifyContent: 'space-around',
    paddingLeft: 8,
    paddingRight: 25,
    alignItems: 'center',
  },
  titleCall: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleMiddle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  box: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',

    '& > svg': {
      marginRight: 8,
    },

    [themeMui.breakpoints.down('sm')]: {
      fontSize: 12,
    },
  },
  boxSubTitle: {
    fontSize: 12,
    fontWeight: 'normal',
    opacity: 0.7,
  },
  searchBarContainer: {
    maxWidth: '70%',
    width: '100%',
    margin: '0 auto',
  },
  helpContainer: ({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    marginRight: 48,

    '& a': {
      color: theme.colors.primaryText,
    },

    [themeMui.breakpoints.down('sm')]: {
      marginRight: 16,
    },
  }),
}))

const useStylesPopOver = makeStyles({
  paper: {
    marginTop: 45,
    width: WIDTH_MENU,
    borderRadius: '0px !important',
  },
  itemLink: ({ theme }) => ({
    position: 'relative',
    display: 'block',
    textDecoration: 'none',
    color: theme.colors.primaryText,
  }),
})

const SubMenu = ({ title, routes, testId }: PropsSubMenu) => {
  const [anchorEl, setAnchorEl] = useState(null)

  const { history } = useReactRouter()
  const [theme]: GlobalState = useGlobalState('theme')

  const handleClick = (event) => setAnchorEl(event.currentTarget)
  const handleClose = () => setAnchorEl(null)
  const handleRoute = (route) => {
    setAnchorEl(null)

    if (!!route.function) route.function()
    else history.push(route.path)
  }

  const classes = useStylesPopOver({ theme })

  return (
    <div style={{ textAlign: 'left', cursor: 'pointer' }}>
      <div data-testid={testId} onClick={handleClick}>
        {title}
      </div>
      <Menu
        id={title}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PopoverClasses={{ paper: classes.paper }}
      >
        {!!routes &&
          routes.map((itemRoute) => {
            const title = formattedMessage({ id: `NavBar.${itemRoute.title}` })

            return (
              <MenuItem
                id={`menu-sub-${itemRoute.path.replace('/', '')}`}
                key={itemRoute.title}
                onClick={() => handleRoute(itemRoute)}
              >
                {title}
              </MenuItem>
            )
          })}
      </Menu>
    </div>
  )
}

const Navbar = () => {
  const { history } = useReactRouter()

  const [theme]: GlobalState = useGlobalState('theme')
  const isMobile = useIsMobile()

  const showSearchBar = history.location.pathname.includes('/result-lawyer')
  const classes = useStyles({ theme, showSearchBar })

  const loggedIn = useTracker(() => !!Meteor.user(), [])
  const profile = useTracker(() => Meteor.user()?.profile, [])

  const username = !!profile && `${profile.firstname} ${profile.lastname}`

  const routeSubMenu = useGetSubRoutes(null)

  const titleUser = !!profile && (
    <div className={classes.titleUser}>
      <div>{username}</div>
      <ExpandMore />
    </div>
  )

  const aboRoutes = routeSubMenu.filter((i) => i.typeRoute === 'abonnement')

  const titleMiddle = !!loggedIn ? (
    <>
      {!!profile && !!profile.admin && (
        <SubMenu
          title={
            <div className={classes.titleUser}>
              BackOffice
              <ExpandMore />
            </div>
          }
          routes={routeSubMenu.filter((i) => i.typeRoute === 'admin')}
        />
      )}
      {aboRoutes.length > 0 && (
        <SubMenu
          title={
            <div className={classes.titleUser}>
              <div className={classes.titleCall}>
                <div>Mes calls</div>
              </div>
              <ExpandMore />
            </div>
          }
          routes={aboRoutes}
        />
      )}
      <SubMenu
        testId="menu-profile"
        title={titleUser}
        routes={routeSubMenu.filter((i) => i.typeRoute === 'profile')}
      />
    </>
  ) : (
    <div className={classes.box} onClick={() => history.push('/login')}>
      {!isMobile && <PermIdentityOutlinedIcon />}

      <div>
        <div>
          <FormattedMessage id={'NavBar.Connection'} defaultMessage={'Se connecter'} />
        </div>
        {!isMobile && (
          <div className={classes.boxSubTitle}>
            <FormattedMessage id={'NavBar.MyCalls'} defaultMessage={'Gérer mes appels'} />
          </div>
        )}
      </div>
    </div>
  )

  const logo = (
    <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
      <Logo height={isMobile ? 28 : 40} />
    </div>
  )

  const help = (
    <div className={classes.helpContainer}>
      <a href={'https://aide.callalawyer.fr'} target={'_blank'}>
        <div className={classes.box}>
          <InfoOutlinedIcon />

          {!isMobile && (
            <div>
              <FormattedMessage id={'NavBar.NeedHelp'} defaultMessage={"Besoin d'aide ?"} />
            </div>
          )}
        </div>
      </a>
    </div>
  )

  const lawyerButton = !Meteor.user() ? (
    <div style={{ marginRight: 32 }}>
      <a href={'https://info.callalawyer.fr/je-suis-avocat'} target={'_blank'}>
        <Button color={'primary'}>
          <span style={{ textTransform: 'none' }}>
            <FormattedMessage id={'Navbar.IAmLawyer'} defaultMessage={'Vous êtes un avocat ?'} />
          </span>
        </Button>
      </a>
    </div>
  ) : null

  const menu = <div className={classes.titleMiddle}>{titleMiddle}</div>

  return (
    <div className={classes.root}>
      <div className={classes.firstLine}>
        {isMobile ? (
          <>
            {help} {logo} {menu}
          </>
        ) : (
          <>
            {logo}
            <div style={{ display: 'flex' }}>
              {lawyerButton} {help} {menu}
            </div>
          </>
        )}
      </div>

      {showSearchBar && !isMobile && (
        <div className={classes.searchBarContainer}>
          <SearchNewContainer />
        </div>
      )}
    </div>
  )
}

export default Navbar
